import React, { Fragment } from 'react';
import { Dialog, Transition } from '@headlessui/react';
import { XMarkIcon, ShoppingBagIcon } from '@heroicons/react/24/outline';
import { useCartStore } from '../store/useCartStore';
import { productsService } from '../services/products';
import { Link } from 'react-router-dom';

export const Cart: React.FC = () => {
  const { items, isOpen, setCartOpen, removeFromCart, updateQuantity, getSubtotal } = useCartStore();

  const subtotal = getSubtotal();

  return (
    <Transition.Root show={isOpen} as={Fragment}>
      <Dialog as="div" className="relative z-50" onClose={setCartOpen}>
        <Transition.Child
          as={Fragment}
          enter="ease-in-out duration-300"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in-out duration-300"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
        </Transition.Child>

        <div className="fixed inset-0 overflow-hidden">
          <div className="absolute inset-0 overflow-hidden">
            <div className="pointer-events-none fixed inset-y-0 right-0 flex max-w-full pl-10">
              <Transition.Child
                as={Fragment}
                enter="transform transition ease-in-out duration-300"
                enterFrom="translate-x-full"
                enterTo="translate-x-0"
                leave="transform transition ease-in-out duration-300"
                leaveFrom="translate-x-0"
                leaveTo="translate-x-full"
              >
                <Dialog.Panel className="pointer-events-auto w-screen max-w-md">
                  <div className="flex h-full flex-col bg-white shadow-2xl">
                    {/* Header */}
                    <div className="bg-gradient-to-r from-[#497aab] to-[#5a8bc4] px-6 py-6">
                      <div className="flex items-center justify-between">
                        <Dialog.Title className="text-2xl font-light text-white tracking-wide">
                          Mon Panier
                        </Dialog.Title>
                        <button
                          type="button"
                          className="p-2 text-white/80 hover:text-white hover:bg-white/10 rounded-full transition-all duration-200"
                          onClick={() => setCartOpen(false)}
                        >
                          <span className="sr-only">Fermer le panneau</span>
                          <XMarkIcon className="h-6 w-6" aria-hidden="true" />
                        </button>
                      </div>
                      {items.length > 0 && (
                        <p className="text-white/90 text-sm mt-2">
                          {items.length} article{items.length > 1 ? 's' : ''} dans votre panier
                        </p>
                      )}
                    </div>

                    <div className="flex-1 overflow-y-auto px-4 py-6 sm:px-6">

                      {items.length === 0 ? (
                        <div className="mt-16 text-center">
                          <div className="inline-block p-6 bg-gray-100 rounded-full mb-4">
                            <ShoppingBagIcon className="h-16 w-16 text-gray-400" />
                          </div>
                          <p className="text-lg text-gray-700 font-medium mb-2">Votre panier est vide</p>
                          <p className="text-sm text-gray-500 mb-8">Découvrez nos magnifiques créations</p>
                          <button
                            onClick={() => setCartOpen(false)}
                            className="px-8 py-3 bg-[#c69c70] text-white hover:bg-[#b88a5f] transition-all duration-300 font-medium tracking-wider shadow-lg hover:shadow-xl"
                          >
                            Découvrir nos bijoux
                          </button>
                        </div>
                      ) : (
                        <div className="mt-8">
                          <div className="flow-root">
                            <ul className="-my-6 divide-y divide-gray-200">
                              {items.map((item) => {
                                const price = item.variation
                                  ? parseFloat(item.variation.price)
                                  : parseFloat(item.product.price);
                                const image = item.variation?.image || item.product.images[0];

                                return (
                                  <li key={`${item.product.id}-${item.variationId || 0}`} className="flex py-6 group">
                                    <div className="h-28 w-28 flex-shrink-0 overflow-hidden rounded-lg border-2 border-gray-200 group-hover:border-[#c69c70] transition-colors duration-200">
                                      {image ? (
                                        <img
                                          src={image.src}
                                          alt={image.alt || item.product.name}
                                          className="h-full w-full object-cover object-center"
                                        />
                                      ) : (
                                        <div className="h-full w-full bg-gray-200 flex items-center justify-center">
                                          <span className="text-gray-400 text-xs">Pas d'image</span>
                                        </div>
                                      )}
                                    </div>

                                    <div className="ml-4 flex flex-1 flex-col">
                                      <div>
                                        <div className="flex justify-between">
                                          <h3 className="text-base font-medium text-gray-900">
                                            <Link
                                              to={`/products/${item.product.slug}`}
                                              onClick={() => setCartOpen(false)}
                                              className="hover:text-[#c69c70] transition-colors"
                                            >
                                              {item.product.name}
                                            </Link>
                                          </h3>
                                        </div>
                                        {item.variation && (
                                          <p className="mt-1 text-sm text-gray-500">
                                            {item.variation.attributes.map(attr => attr.option).join(', ')}
                                          </p>
                                        )}
                                        <p className="mt-2 text-lg font-semibold text-[#497aab]">
                                          {productsService.getFormattedPrice(price * item.quantity)}
                                        </p>
                                      </div>
                                      <div className="flex flex-1 items-end justify-between text-sm mt-4">
                                        <div className="flex items-center gap-1 bg-gray-100 rounded-lg p-1">
                                          <button
                                            onClick={() => updateQuantity(
                                              item.product.id,
                                              item.quantity - 1,
                                              item.variationId
                                            )}
                                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-[#c69c70] hover:bg-white rounded transition-all duration-200"
                                          >
                                            −
                                          </button>
                                          <span className="w-12 text-center font-medium text-gray-900">
                                            {item.quantity}
                                          </span>
                                          <button
                                            onClick={() => updateQuantity(
                                              item.product.id,
                                              item.quantity + 1,
                                              item.variationId
                                            )}
                                            className="w-8 h-8 flex items-center justify-center text-gray-600 hover:text-[#c69c70] hover:bg-white rounded transition-all duration-200"
                                          >
                                            +
                                          </button>
                                        </div>

                                        <button
                                          type="button"
                                          onClick={() => removeFromCart(item.product.id, item.variationId)}
                                          className="font-medium text-gray-500 hover:text-red-600 transition-colors duration-200"
                                        >
                                          Supprimer
                                        </button>
                                      </div>
                                    </div>
                                  </li>
                                );
                              })}
                            </ul>
                          </div>
                        </div>
                      )}
                    </div>

                    {items.length > 0 && (
                      <div className="border-t-2 border-gray-200 bg-gray-50 px-6 py-6">
                        <div className="flex justify-between items-center mb-2">
                          <p className="text-lg font-light text-gray-700">Sous-total</p>
                          <p className="text-2xl font-semibold text-[#497aab]">
                            {productsService.getFormattedPrice(subtotal)}
                          </p>
                        </div>
                        <p className="text-xs text-gray-500 mb-6">
                          Frais de livraison et taxes calculés à la commande.
                        </p>
                        
                        <Link
                          to="/checkout"
                          onClick={() => setCartOpen(false)}
                          className="block w-full text-center px-6 py-4 bg-gradient-to-r from-[#c69c70] to-[#b88a5f] text-white font-medium tracking-wider shadow-xl hover:shadow-2xl transition-all duration-300 hover:from-[#b88a5f] hover:to-[#a67a51] mb-4"
                        >
                          PASSER LA COMMANDE
                        </Link>
                        
                        <button
                          type="button"
                          className="w-full text-center px-6 py-3 border-2 border-[#497aab] text-[#497aab] font-medium hover:bg-[#497aab] hover:text-white transition-all duration-300"
                          onClick={() => setCartOpen(false)}
                        >
                          Continuer mes achats
                        </button>
                      </div>
                    )}
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </div>
      </Dialog>
    </Transition.Root>
  );
};
